/**
 * Classifies a batch of data operations so save paths can scope search-index
 * rebuilds and React Query invalidation without weakening correctness for
 * structural / naming changes (which can renumber labels globally).
 */

export type OperationImpact = 'content-only' | 'structural';

/** Update fields that only change searchable/display content of a block. */
export const ContentOnlyUpdateFieldKeys = [
  'text',
  'items',
  'rows',
  'caption',
  'src',
  'alt',
  'width',
  'height',
  'alignment',
  'meta',
  'columns',
  'headerRowCount',
  'rowHeaderCol',
] as const;

const contentOnlyUpdateFieldSet: ReadonlySet<string> = new Set(
  ContentOnlyUpdateFieldKeys,
);

export type ClassifiableDataOperation = {
  op: string;
  fields?: Record<string, unknown> | null;
};

/**
 * Returns `content-only` when every operation is an `update` whose fields are
 * exclusively content/asset keys. Any add/remove/move/reorder, or an update
 * that touches prefix/title/style/numbering/order, is `structural`.
 */
export function classifyOperationsImpact(
  operations: readonly ClassifiableDataOperation[],
): OperationImpact {
  for (const operation of operations) {
    if (operation.op !== 'update') return 'structural';
    const fields = operation.fields;
    if (!fields || typeof fields !== 'object') continue;
    for (const key of Object.keys(fields)) {
      if (!contentOnlyUpdateFieldSet.has(key)) return 'structural';
    }
  }
  return 'content-only';
}
